import type { ChatMessage, ContentBlock, VisualizationData } from '../../../../nebula/types';
import type { NebulaMessage } from '../../../../types/nebula-insights';
import {
  formatMessageContentForDisplay,
  getMessageVisualizations,
  stripVisualizationJsonFromText,
  stripXmlWrapperTags,
} from '../utils';

function isVisualizationData(value: unknown): value is VisualizationData {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  const config = obj.config;
  if (!config || typeof config !== 'object') return false;
  return (
    typeof (config as Record<string, unknown>).type === 'string' &&
    Array.isArray(obj.data)
  );
}

function cleanTextForExport(text: string): string {
  return stripVisualizationJsonFromText(stripXmlWrapperTags(text)).trim();
}

function extractTextFromContentBlock(block: unknown): string {
  if (typeof block === 'string') return cleanTextForExport(block);
  if (!block || typeof block !== 'object') return '';

  const obj = block as Record<string, unknown>;
  if (obj.type === 'text') {
    if (typeof obj.text === 'string') return cleanTextForExport(obj.text);
    if (typeof obj.content === 'string') return cleanTextForExport(obj.content);
  }
  if (typeof obj.text === 'string') return cleanTextForExport(obj.text);
  return '';
}

function appendVisualizations(message: NebulaMessage, blocks: ContentBlock[]): void {
  for (const viz of getMessageVisualizations(message)) {
    const alreadyAdded = blocks.some(
      (block) =>
        block.type === 'visualization' &&
        JSON.stringify(block.data) === JSON.stringify(viz)
    );
    if (!alreadyAdded) {
      blocks.push({ type: 'visualization', data: viz });
    }
  }
}

function buildContentBlocks(message: NebulaMessage): ContentBlock[] {
  const blocks: ContentBlock[] = [];

  if (Array.isArray(message.content)) {
    for (const block of message.content) {
      if (typeof block === 'string') {
        const text = cleanTextForExport(block);
        if (text) blocks.push({ type: 'text', content: text });
        continue;
      }

      if (!block || typeof block !== 'object') continue;
      const obj = block as Record<string, unknown>;

      if (obj.type === 'visualization' && isVisualizationData(obj.data)) {
        blocks.push({ type: 'visualization', data: obj.data });
        continue;
      }

      const text = extractTextFromContentBlock(block);
      if (text) blocks.push({ type: 'text', content: text });
    }
  } else {
    const text = formatMessageContentForDisplay(message.content);
    if (text) blocks.push({ type: 'text', content: text });
  }

  appendVisualizations(message, blocks);
  return blocks;
}

export function mapNebulaMessagesToChatMessages(messages: NebulaMessage[]): ChatMessage[] {
  return messages
    .filter((message): message is NebulaMessage & { role: 'user' | 'assistant' } =>
      message.role === 'user' || message.role === 'assistant'
    )
    .map((message) => {
      const contentBlocks = buildContentBlocks(message);
      const content = contentBlocks
        .filter((block): block is Extract<ContentBlock, { type: 'text' }> => block.type === 'text')
        .map((block) => block.content)
        .join('\n\n');

      return {
        id: message.id,
        role: message.role,
        content,
        contentBlocks: contentBlocks.length > 0 ? contentBlocks : undefined,
        timestamp: new Date(message.createdAt),
      };
    });
}
